import type { AddressSuggestion } from "./address-search.js";

/**
 * Which suggestion a typed-but-not-picked address stands for — a 1:1 port of
 * formvu-web's widget rule (`find-fully-typed-match.ts` +
 * `pickAutoSelectSuggestion` in `address-input.tsx`), so an embed and the
 * widget's address screen settle the same text on the same address.
 */

/** Uppercase and collapse punctuation/whitespace so token sets compare cleanly. */
function normalizeAddressText(text: string): string {
  return text
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();
}

/** The last 5-digit group past the street number, i.e. a typed ZIP if any. */
function typedZipOf(typed: string): string | undefined {
  return typed
    .replace(/^\s*\d+/, "")
    .match(/\b\d{5}\b/g)
    ?.pop();
}

/**
 * Whether the typed text spells out the suggestion's street line: same street
 * number and every word of the street line present. Checked against both the
 * canonical `components.primary_line` (Mapbox expands abbreviations — "Court",
 * drops directionals) and the display `line1` ("3013 W Seabrook Ct"), since
 * typed text usually mirrors one of the two forms.
 */
export function matchesTypedStreet(typed: string, suggestion: AddressSuggestion): boolean {
  const c = suggestion.components;
  if (!c) return false;

  const typedNumber = /^\s*(\d+)/.exec(typed)?.[1];
  if (!typedNumber || /^\s*(\d+)/.exec(c.primary_line)?.[1] !== typedNumber) return false;

  const typedTokens = new Set(normalizeAddressText(typed).split(" "));
  const spellsOut = (streetLine: string) =>
    normalizeAddressText(streetLine)
      .split(" ")
      .every((token) => typedTokens.has(token));

  return spellsOut(c.primary_line) || spellsOut(suggestion.line1);
}

/**
 * The suggestion whose structured address the typed text fully spells out, if
 * exactly one does. "Fully" means: the street line matches (see
 * `matchesTypedStreet`) and the ZIP (when typed) or city matches — a complete
 * typed address, not a prefix. Ambiguity (2+ matches) returns null.
 */
export function findFullyTypedMatch(
  typed: string,
  suggestions: AddressSuggestion[],
): AddressSuggestion | null {
  const typedTokens = new Set(normalizeAddressText(typed).split(" "));
  const typedZip = typedZipOf(typed);

  const matches = suggestions.filter((s) => {
    if (!matchesTypedStreet(typed, s)) return false;

    // The typed text must anchor the rest of the address too.
    if (typedZip) return typedZip === s.components?.zip_code;
    const cityTokens = normalizeAddressText(s.components?.city ?? "").split(" ");
    return cityTokens.every((token) => typedTokens.has(token));
  });

  return matches.length === 1 ? matches[0]! : null;
}

const UNIT_PATTERN =
  /(?:\b(apt|apartment|unit|ste|suite|bldg|building|floor|rm|room|lot|spc|space|trlr)\b\.?|(#))\s*([a-z0-9][a-z0-9-]*(?:\s+[a-z](?=\s*(?:,|$)))?)/gi;

/** The unit Mapbox reports for a suggestion ("APT 5A" → "5A"), or null. */
export function suggestionUnitId(suggestion: AddressSuggestion): string | null {
  // The designator off, the whole identifier kept ("APT 5 B" → "5B").
  const id = suggestion.secondary_line
    .trim()
    .replace(/^[a-z]+\.?\s+(?=[a-z0-9#])/i, "")
    .replace(/[#\s]/g, "");
  return id ? id.toUpperCase() : null;
}

/**
 * The unit identifiers the visitor typed, judged against one suggestion: a
 * designator (APT, UNIT, STE, #, …) plus whatever follows it, unless that
 * word pair is part of the suggestion's own street or city — "10 Space View
 * Rd" and "Ste. Genevieve" are words of the address, "APT PH", "APT 5B" and
 * "#12" are units, written anywhere ("70 Washington St, APT 5B, Brooklyn").
 */
export function typedUnitIds(typed: string, suggestion: AddressSuggestion): string[] {
  // line1 shows the suggestion's own unit ("70 Washington St APT 5A"); only
  // its street words count as address words.
  const line1Street = suggestion.secondary_line
    ? suggestion.line1.replace(suggestion.secondary_line, "")
    : suggestion.line1;
  const addressWords = normalizeAddressText(
    [suggestion.components?.primary_line ?? "", line1Street, suggestion.components?.city ?? ""].join(" "),
  ).split(" ");
  const isAddressWordPair = (first: string, second: string) =>
    addressWords.some((word, i) => word === first && addressWords[i + 1] === second);

  const ids: string[] = [];
  for (const match of typed.matchAll(UNIT_PATTERN)) {
    const designator = match[1];
    const words = match[3]!.toUpperCase().split(/\s+/);
    // Street words first ("Space View N"), before a spaced id is joined.
    if (designator && isAddressWordPair(designator.toUpperCase(), words[0]!)) continue;
    const id = words.join("");
    ids.push(id);
  }
  return ids;
}

/**
 * Whether the typed text and the suggestion agree on the unit: neither has
 * one, or every unit the visitor typed is the suggestion's unit. `primary_line`
 * never carries the unit, so a street match alone would let "APT 5B" settle on
 * the "APT 5A" suggestion — the widget catches that later with Lob and its
 * unit prompt; the embed has neither, so it must not pick across units. A unit
 * only the suggestion has is a disagreement too: the visitor never said it.
 * Erring toward "disagree" only costs an auto-selection — the typed text still
 * submits — never a unit the visitor wrote.
 */
export function unitsAgree(typed: string, suggestion: AddressSuggestion): boolean {
  const typedIds = typedUnitIds(typed, suggestion);
  const suggested = suggestionUnitId(suggestion);
  if (!suggested) return typedIds.length === 0;
  return typedIds.length > 0 && typedIds.every((id) => id.replace(/^#/, "") === suggested);
}

// A unit written inside a comma-separated part ("APT 5", "# 5A", "#5A",
// "Lot 12", "Unit B", …): a designator and an identifier with a digit or of
// one or two letters, so a city that starts like one ("Ste. Genevieve",
// "Lot Acres") stays a city.
// A typed ZIP (ZIP+4 too) — never five digits that follow a unit designator
// ("Apt 12345"). A capture group, not a lookbehind: Safari before 16.4 cannot
// parse a lookbehind, and one regex literal would break the whole bundle.
const ZIP_OR_UNIT_NUMBER =
  /(#\s*|\b(?:apt|apartment|unit|ste|suite|bldg|building|fl|floor|rm|room|lot|spc|space|trlr)\b\.?\s*)?\b\d{5}(?:-\d{4})?\b/gi;

function removeTypedZip(text: string): string {
  return text.replace(ZIP_OR_UNIT_NUMBER, (match, unit: string | undefined) => (unit ? match : " "));
}

const UNIT_IN_PART =
  /(?:#|\b(?:apt|apartment|unit|ste|suite|bldg|building|fl|floor|rm|room|lot|spc|space|trlr)\b\.?(?:\s*#)?)\s*(?:[a-z0-9-]*\d[a-z0-9-]*(?:\s+[a-z](?=\s*$))?|[a-z]{1,2})\b/gi;

// A spaced letter after the number ("Apt 5 A") is part of the unit only at
// the end of its part ("…, Apt 5 A, Blaine"). Before more words it is
// indistinguishable from a city's initial ("Apt 5 N Las Vegas"), and the safe
// reading keeps it in the city — a suggestion that is not that city is then
// not picked. A suggestion whose own unit is "APT 5 A" still matches (its
// unit is removed whole first).

// A part that is a whole named unit ("Apt Rear", "Unit North", "Suite PH"):
// only designators that never begin a place name — "Ste." (Ste. Genevieve),
// "Lot" and "Space" keep the identifier rule above.
const NAMED_UNIT_PART =
  /^(?:#|(?:apt|apartment|unit|suite|bldg|building|fl|floor|rm|room|spc|trlr)\b\.?\s*#?)\s*[a-z0-9-]+$/i;

/**
 * Whether anything the visitor typed past the street contradicts the
 * suggestion: a typed ZIP must be its ZIP, and a typed city — the first
 * comma-separated part after the street, with any unit in it removed — must be its
 * city (or the city its locality line displays), whole, optionally followed
 * by its state; a part that is only the state is fine. Both anchors are
 * checked: a matching ZIP does not excuse a wrong city. Text with no ZIP and
 * no such part says nothing beyond the street, so it never contradicts. Same
 * rule as the widget's (formvu-web `typedAnchorsAgree`).
 *
 * Guards the sole-suggestion pick: Mapbox can answer "9999 Washington Street,
 * Brooklyn" with nothing but a Washington Street in Blaine, MN. Names compare
 * whole, so "Richland" never accepts "West Richland".
 */
export function typedAnchorsAgree(typed: string, suggestion: AddressSuggestion): boolean {
  const c = suggestion.components;
  // A unit identifier is not a ZIP ("APT 12345"): read the ZIP with units removed.
  const zip = typedZipOf(typed.replace(UNIT_PATTERN, " "));
  if (zip && zip !== c?.zip_code) return false;

  // The suggestion's own unit is a unit even when it reads like a word
  // ("APT PENTHOUSE"); any other unit needs an identifier (UNIT_IN_PART).
  const ownUnit = normalizeAddressText(suggestion.secondary_line);
  // A country written as the LAST part is not a city ("…, 11201, United States").
  const countries = new Set([
    "UNITED STATES",
    "UNITED STATES OF AMERICA",
    "USA",
    "US",
    "CANADA",
    normalizeAddressText(suggestion.line2.split(",").pop() ?? ""),
  ]);
  const typedCityWith = (unitPattern: RegExp) =>
    typed
      .split(",")
      .slice(1)
      .map((segment) => segment.trim())
      // Empty parts ("…, United States,") are no position at all.
      .filter(Boolean)
      .filter((segment) => !NAMED_UNIT_PART.test(segment))
      .map((segment) => {
        // The ZIP (ZIP+4 too) first, while its dash still binds the "+4" —
        // never a unit's number ("Apt 12345").
        const part = removeTypedZip(segment);
        // The suggestion's own unit first, whole ("APT 5 A"), then any other.
        const words = ` ${normalizeAddressText(part)} `;
        const rest = ownUnit && words.includes(` ${ownUnit} `) ? words.replace(` ${ownUnit} `, " ") : part;
        return normalizeAddressText(rest.replace(unitPattern, " "));
      })
      .find((part, i, parts) => part && !(i === parts.length - 1 && countries.has(part)));
  const typedCity = typedCityWith(UNIT_IN_PART);
  if (!typedCity) return true;

  // line2 is Mapbox's "City, State ZIP, Country".
  const [displayedCity = "", displayedState = ""] = suggestion.line2.split(",");
  const cities = [c?.city ?? "", displayedCity].map(normalizeAddressText).filter(Boolean);
  const states = [c?.state ?? "", displayedState.replace(/\d/g, " ")]
    .map(normalizeAddressText)
    .filter(Boolean);

  const agrees = (reading: string) =>
    states.includes(reading) ||
    cities.some((city) => reading === city || states.some((state) => reading === `${city} ${state}`));
  return agrees(typedCity);
}

/**
 * The suggestion an auto-selection (Enter with nothing highlighted, or submit)
 * picks for typed text, or null. A sole result is trusted the same way a click
 * on it would be — Mapbox already fuzzy-matched the typed text — provided it
 * has structured parts; with several results only the one the typed text fully
 * spells out qualifies. Either way the units must agree (`unitsAgree`) — a
 * deliberate addition over the widget, which re-checks units with Lob.
 */
export function autoSelectSuggestion(
  typed: string,
  suggestions: AddressSuggestion[],
): AddressSuggestion | null {
  const text = typed.trim();
  if (!text || suggestions.length === 0) return null;
  // A sole result is trusted like a click — unless the city or ZIP the
  // visitor typed says it is a different place.
  const candidate =
    suggestions.length === 1
      ? suggestions[0]!.components && typedAnchorsAgree(text, suggestions[0]!)
        ? suggestions[0]!
        : null
      : findFullyTypedMatch(text, suggestions);
  return candidate && unitsAgree(text, candidate) ? candidate : null;
}

const STREET_NOISE = new Set([
  "N", "S", "E", "W", "NE", "NW", "SE", "SW", "NORTH", "SOUTH", "EAST", "WEST",
  "ST", "STREET", "AVE", "AVENUE", "RD", "ROAD", "DR", "DRIVE", "LN", "LANE", "CT", "COURT",
  "BLVD", "BOULEVARD", "WAY", "PL", "PLACE", "TER", "TERRACE", "PKWY", "PARKWAY", "CIR", "CIRCLE", "HWY", "HIGHWAY",
]);

/**
 * Whether a lookup describes the street the visitor typed: the same house
 * number, and the typed street's name words (directionals and suffixes
 * aside — "W"/"West", "St"/"Street" differ by form) all in the suggestion's
 * street. Looser than `matchesTypedStreet` on purpose: a half-typed
 * "70 Washington" still stands for "70 Washington Street", but never for
 * "72 Washington Street" or "70 Lincoln Street".
 */
export function typedStreetAgrees(typed: string, suggestion: AddressSuggestion): boolean {
  const c = suggestion.components;
  if (!c) return false;
  const typedNumber = /^\s*(\d+)/.exec(typed)?.[1];
  if (!typedNumber || /^\s*(\d+)/.exec(c.primary_line)?.[1] !== typedNumber) return false;
  const known = new Set(normalizeAddressText(`${c.primary_line} ${suggestion.line1}`).split(" "));
  const street = normalizeAddressText((typed.split(",")[0] ?? "").replace(UNIT_PATTERN, " "))
    .split(" ")
    .slice(1)
    .filter((word) => word && !STREET_NOISE.has(word));
  return street.every((word) => known.has(word));
}

/**
 * The whole unit the visitor typed, as written ("APT 5 B", "#12"), for a
 * verification request: the designator that `typedUnitIds` read and its full
 * identifier, nothing more. `typedUnitIds` normalizes the identifier ("5 B"
 * → "5B"), which would verify apartment 5 for a typed "5 B" if sent as is.
 * Empty when the visitor typed no unit.
 */
export function typedUnitText(typed: string, suggestion: AddressSuggestion): string {
  const id = typedUnitIds(typed, suggestion)[0];
  if (!id) return "";
  const idPattern = [...id].map((char) => char.replace(/[^A-Z0-9]/g, "\\$&")).join("\\s*");
  const unit = new RegExp(
    `(?:\\b(?:apt|apartment|unit|ste|suite|bldg|building|floor|rm|room|lot|spc|space|trlr)\\b\\.?|#)\\s*#?\\s*${idPattern}(?![a-z0-9])`,
    "i",
  );
  return unit.exec(typed)?.[0].trim() ?? id;
}
